/** @prettier */
import classNames from 'classnames';
import PropTypes from 'prop-types';
import React from 'react';
import { Link } from 'react-router-dom';

import ExternalLink from '@xanadu/ui-shared/components/widgets/ExternalLink';
import {
  ALDERNEY_URL,
  BET_BLOCKER as BET_BLOCKER_URL,
  GAMBLING_CONTROL as GAMBLING_CONTROL_URL,
  GAME_CARE_URL,
  GAMSTOP_URL,
  GRAI_URL,
  PLUS18_URL,
  TTTT_URL,
} from '@xanadu/ui-shared/constants';

import LOGO_PLUS18 from '../../../../../images/footer/Logo_18Plus_Light.svg';
import LOGO_ALDERNEY from '../../../../../images/footer/Logo_Alderney_Light.svg';
import LOGO_BET_BLOCKER from '../../../../../images/footer/Logo_BetBlocker_Light.svg';
import LOGO_GAME_CARE from '../../../../../images/footer/Logo_GamCare_Light.svg';
import LOGO_GAMBLING_CONTROL from '../../../../../images/footer/Logo_GamblingCommission_Light.svg';
import LOGO_GAMSTOP from '../../../../../images/footer/Logo_Gamstop_Light.svg';
import LOGO_TTTT from '../../../../../images/footer/Logo_TimeToThink_Light.svg';
import LOGO_GRAI from '../../../../../images/footer/grai_logo.png';
import LOGO_ALDERNEY_COLORED from '../../../../../images/various/alderney_dark.svg';
// TODO: add betblocker logo
import LOGO_GAME_CARE_COLORED from '../../../../../images/various/gamcare_dark.svg';
import LOGO_GAMSTOP_COLORED from '../../../../../images/various/gamstop_dark.svg';
import LOGO_PLUS18_COLORED from '../../../../../images/various/plus18_dark.svg';
import LOGO_TTTT_COLORED from '../../../../../images/various/taketimetothink_dark.svg';
import LOGO_GAMBLING_CONTROL_COLORED from '../../../../../images/various/ukgc_dark.svg';

import LicenseContainer from '../LicenseContainer/LicenseContainer';

import styles from './UKRegulators.module.scss';

const regulatorLogos = {
  ALDERNEY: LOGO_ALDERNEY,
  GAMBLING_CONTROL: LOGO_GAMBLING_CONTROL,
  GAME_CARE: LOGO_GAME_CARE,
  GAMSTOP: LOGO_GAMSTOP,
  TTTT: LOGO_TTTT,
  PLUS18: LOGO_PLUS18,
  BET_BLOCKER: LOGO_BET_BLOCKER,
  ALDERNEY_COLORED: LOGO_ALDERNEY_COLORED,
  GAME_CARE_COLORED: LOGO_GAME_CARE_COLORED,
  GAMBLING_CONTROL_COLORED: LOGO_GAMBLING_CONTROL_COLORED,
  GAMSTOP_COLORED: LOGO_GAMSTOP_COLORED,
  PLUS18_COLORED: LOGO_PLUS18_COLORED,
  TTTT_COLORED: LOGO_TTTT_COLORED,
  GRAI: LOGO_GRAI,
  GRAI_COLORED: LOGO_GRAI,
};

const regulatorURLs = {
  ALDERNEY: ALDERNEY_URL,
  GAMBLING_CONTROL: GAMBLING_CONTROL_URL,
  GAME_CARE: GAME_CARE_URL,
  GAMSTOP: GAMSTOP_URL,
  TTTT: TTTT_URL,
  PLUS18: PLUS18_URL,
  BET_BLOCKER: BET_BLOCKER_URL,
  GRAI: GRAI_URL,
};

const UKRegulators = ({
  title,
  logosPosition = 'top',
  shrunk = false,
  centered = false,
  noText = false,
  displayedRegulators = [],
  colored = false,
}) => {
  const logosClass = classNames({
    [styles.regulationsLogos]: true,
    [styles['regulationsLogos--centered']]: centered,
  });

  const regulatorsClass = classNames({
    [styles.regulations]: true,
    [styles['regulations--shrunk']]: shrunk,
    [styles['regulations--centered']]: centered,
  });

  const logos = (
    <div className={logosClass}>
      {displayedRegulators.map((regulator) => {
        const externalRegExp = /:\/\//;
        const RegulatorLogo = colored
          ? regulatorLogos[`${regulator}_COLORED`]
          : regulatorLogos[regulator];
        const regulatorUrl = regulatorURLs[regulator];
        const isPNG = regulator === 'GRAI'; // GRAI logo is PNG, not SVG

        if (!RegulatorLogo) {
          return null;
        }

        if (!externalRegExp.test(regulatorUrl)) {
          return (
            <Link to={regulatorUrl} key={regulatorUrl}>
              {isPNG ? (
                <img src={RegulatorLogo} alt={regulator} style={{ height: '24px' }} />
              ) : (
                <RegulatorLogo />
              )}
            </Link>
          );
        }
        return (
          <ExternalLink href={regulatorUrl} key={regulatorUrl}>
            {isPNG ? (
              <img src={RegulatorLogo} alt={regulator} style={{ height: '64px' }} />
            ) : (
              <RegulatorLogo />
            )}
          </ExternalLink>
        );
      })}
    </div>
  );

  return (
    <div className={regulatorsClass}>
      {title ? <div className={styles.title}>{title}</div> : null}
      {logosPosition === 'top' ? logos : null}
      {noText ? null : <LicenseContainer />}
      {logosPosition === 'bottom' ? logos : null}
    </div>
  );
};

UKRegulators.propTypes = {
  title: PropTypes.string,
  logosPosition: PropTypes.oneOf(['top', 'bottom']),
  shrunk: PropTypes.bool,
  centered: PropTypes.bool,
  noText: PropTypes.bool,
  displayedRegulators: PropTypes.array,
  colored: PropTypes.bool,
};

export default UKRegulators;
